// Eigenbau: Ereignisstrom des Projekts, nach Tag gruppiert (Paket 12)
//
// In der Form der Vorlage, Abschnitt PULS: Titel, ein Satz, eine Filterreihe in
// Mono, danach je Tag eine Rubrik und darunter Zeilen mit Zeitspalte,
// Zeitstrahl und Ereignissatz.
//
// **Der Satz wird aus Teilen gebaut, nicht aus `beschreibung` übernommen.** Die
// Vorlage zeichnet drei Auszeichnungen: Akteur in Gewicht 500, Vorgang ruhig,
// Objekt in Mono-Akzent. `beschreibeEreignis` liefert dagegen einen fertigen
// Satz mit *generischem* Akteur („das System hat …"), und beides zusammen
// ergäbe „Malwin Weber das System hat aufgabe geändert". Zusammengesetzt wird
// deshalb aus `akteurName`, `VORGANG_LABELS[vorgang]` und `betrifft.art` —
// dieselben Teile, aus denen der Server seinen Satz baut.
//
// Wortstellung: `{Akteur} hat {Objekt} {Vorgang}`. Die Vorlage stellt den
// Vorgang vor das Objekt, aber `VORGANG_LABELS` sind Partizipien („geändert",
// „angelegt") — „hat geändert aufgabe" wäre kein Deutsch. Die Auszeichnungen
// sind dieselben, nur die Reihenfolge folgt der Sprache.
//
// Die drei Auswahlfelder für Akteursart, Vorgang und Objektart sind entfallen;
// die Vorlage zeigt eine Reihe mit vier Zeitspannen. Die Suchparameter
// `?akteur=`, `?vorgang=` und `?art=` bleiben gültig und werden weiter von
// `validiereProjektSuche` behandelt, geteilte Verweise brechen also nicht.

import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  VORGANG_LABELS,
  type Akteursart,
  type Objektart,
  type Vorgang,
} from "@cubic/shared";
import { api } from "@/lib/api";
import { schluessel } from "@/lib/abfragen";
import { cx, focusRing } from "@/lib/utils";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { verweisKlassen } from "@/components/entwurf/Verweis";

export interface PulsFilter {
  akteurArt?: Akteursart | undefined;
  vorgang?: Vorgang | undefined;
  art?: Objektart | undefined;
  seit?: "24h" | "7t" | "30t" | "alles" | undefined;
}

function seitDatum(wert: PulsFilter["seit"]): Date | undefined {
  if (!wert || wert === "alles") return undefined;
  const tage = wert === "24h" ? 1 : wert === "30t" ? 30 : 7;
  return new Date(Date.now() - tage * 24 * 60 * 60 * 1_000);
}

function tagKey(wert: string): string {
  return new Date(wert).toLocaleDateString("de-DE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function akteurLabel(art: Akteursart, name: string | null): string {
  const person = name ?? "Gelöschte Person";
  switch (art) {
    case "metarr":
      return `Metarr · ${person}`;
    case "mcp":
      return `MCP · ${person}`;
    case "system":
      return "System";
    case "person":
      return person;
  }
}

/** Die Herkunft rechts außen — im Entwurf `quelle`. */
const QUELLE: Readonly<Record<Akteursart, string>> = {
  person: "Oberfläche",
  metarr: "Metarr",
  mcp: "Werkzeug",
  system: "System",
};

/**
 * Der Punkt am Zeitstrahl: Tintendichte nach Akteursart, keine Farbe.
 *
 * Der Akzent bleibt Aktion, Navigation und Überfälligkeit vorbehalten — ein
 * Ereignisstrom verlangt keine Entscheidung, er berichtet.
 */
const PUNKT: Readonly<Record<Akteursart, string>> = {
  person: "bg-tinte",
  metarr: "bg-tinte4",
  mcp: "bg-tinte4",
  system: "bg-ruhe",
};

const ZEITSPANNEN: ReadonlyArray<{
  wert: NonNullable<PulsFilter["seit"]>;
  label: string;
}> = [
  { wert: "24h", label: "24 Stunden" },
  { wert: "7t", label: "7 Tage" },
  { wert: "30t", label: "30 Tage" },
  { wert: "alles", label: "Alles" },
];

export function PulsReiter({
  projektId,
  filter = {},
}: {
  projektId: string;
  filter?: PulsFilter;
}) {
  const navigate = useNavigate();
  const seit = useMemo(() => seitDatum(filter.seit), [filter.seit]);
  const verlauf = useInfiniteQuery({
    queryKey: schluessel.verlauf(projektId, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.verlauf.liste.query({
        projektId,
        betrifft: filter.art ? { art: filter.art } : undefined,
        akteurArt: filter.akteurArt,
        vorgang: filter.vorgang,
        seit,
        cursor: pageParam,
        grenze: 50,
      }),
    getNextPageParam: (seite) => seite.naechsterCursor ?? undefined,
    enabled: Boolean(projektId),
  });

  const eintraege = useMemo(
    () => verlauf.data?.pages.flatMap((seite) => seite.eintraege) ?? [],
    [verlauf.data?.pages],
  );
  const gruppen = useMemo(() => {
    const map = new Map<string, typeof eintraege>();
    for (const eintrag of eintraege) {
      const tag = tagKey(eintrag.geschehenAm);
      map.set(tag, [...(map.get(tag) ?? []), eintrag]);
    }
    return [...map.entries()];
  }, [eintraege]);

  const aktiveSpanne = filter.seit ?? "alles";

  const setzeSpanne = (wert: NonNullable<PulsFilter["seit"]>) => {
    void navigate({
      to: "/projekte/$id",
      params: { id: projektId },
      search: (vorher) => ({
        ...vorher,
        reiter: "puls",
        seit: wert === "alles" ? undefined : wert,
      }),
    });
  };

  return (
    <div className="max-w-[880px]">
      <Seitenkopf kontext="Ereignisstrom des Projekts" breite={880}>
        Puls
      </Seitenkopf>
      <p className="mt-[18px] max-w-[46ch] text-[15px] font-light leading-[1.65] text-tinte3">
        Jede Zustandsänderung schreibt ein Ereignis. Hier stehen sie alle,
        ungefiltert.
      </p>

      {/*
        Die Filterreihe der Vorlage: Mono, 20px Abstand, der aktive Punkt trägt
        eine Haarlinie unter sich und Gewicht 500 — kein Rahmen, keine Fläche.
      */}
      <div className="mt-[34px] flex gap-5">
        {ZEITSPANNEN.map((spanne) => {
          const aktiv = spanne.wert === aktiveSpanne;
          return (
            <button
              key={spanne.wert}
              type="button"
              aria-pressed={aktiv}
              onClick={() => setzeSpanne(spanne.wert)}
              className={cx(
                "pb-1 font-mono text-[12.5px] transition-colors duration-150 ease-aus",
                aktiv
                  ? "font-medium text-tinte shadow-[inset_0_-1px_0_0_var(--tinte)]"
                  : "text-tinte4 hover:text-tinte3",
                focusRing,
              )}
            >
              {spanne.label}
            </button>
          );
        })}
      </div>

      {verlauf.isLoading ? (
        <div className="mt-13">
          <Mikrolabel>Wird geladen …</Mikrolabel>
        </div>
      ) : gruppen.length === 0 ? (
        <div className="mt-13">
          <Mikrolabel>
            In dieser Zeitspanne ist nichts geschehen.
          </Mikrolabel>
        </div>
      ) : null}

      {gruppen.map(([tag, tagEintraege]) => (
        <section key={tag} className="mt-13">
          <Rubrik>{tag}</Rubrik>
          {tagEintraege.map((eintrag) => {
            const art = eintrag.akteur.art;
            return (
              <div
                key={eintrag.id}
                className="flex items-start border-b border-linie"
              >
                <span className="w-[58px] flex-none py-[17px] font-mono text-[11.5px] tabular-nums text-tinte4">
                  <time dateTime={eintrag.geschehenAm}>
                    {new Date(eintrag.geschehenAm).toLocaleTimeString("de-DE", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </time>
                </span>
                {/*
                  Der Zeitstrahl: eine durchlaufende Haarlinie und ein Punkt auf
                  der Höhe der ersten Textzeile. `self-stretch`, damit die Linie
                  die ganze Zeile trägt und zwischen den Zeilen nicht abreißt.
                */}
                <span
                  aria-hidden="true"
                  className="relative w-[26px] flex-none self-stretch"
                >
                  <span className="absolute inset-y-0 left-3 w-px bg-linie2" />
                  <span
                    className={cx(
                      "absolute left-[9px] top-[21px] size-[7px]",
                      PUNKT[art],
                    )}
                  />
                </span>
                <span className="min-w-0 flex-1 py-[17px] pl-[18px]">
                  <span className="block text-[14.5px] font-light leading-[1.45]">
                    <span className="font-medium">
                      {akteurLabel(art, eintrag.akteurName)}
                    </span>{" "}
                    hat{" "}
                    <span className="font-mono text-[13px] text-akzent">
                      {eintrag.betrifft.art}
                    </span>{" "}
                    {VORGANG_LABELS[eintrag.vorgang] ?? eintrag.vorgang}
                  </span>
                  {eintrag.aenderungen.length > 0 ? (
                    <span className="mt-1.5 block font-mono text-[12px] text-tinte4">
                      {eintrag.aenderungen
                        .map(
                          (a) =>
                            `${a.feld}: ${a.vorher ?? "—"} → ${a.nachher ?? "—"}`,
                        )
                        .join(" · ")}
                    </span>
                  ) : null}
                  {eintrag.hinweis ? (
                    <span className="mt-1.5 block font-mono text-[12px] text-tinte4">
                      {eintrag.hinweis}
                    </span>
                  ) : null}
                </span>
                <span className="flex-none py-[17px] font-mono text-[11px] text-tinte4">
                  {QUELLE[art]}
                </span>
              </div>
            );
          })}
        </section>
      ))}

      {verlauf.hasNextPage ? (
        <button
          type="button"
          disabled={verlauf.isFetchingNextPage}
          onClick={() => void verlauf.fetchNextPage()}
          className={cx("mt-8", verweisKlassen())}
        >
          {verlauf.isFetchingNextPage ? "Lade …" : "Ältere laden"}
        </button>
      ) : null}
    </div>
  );
}
